import React from 'react'
import { useParams } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_TYPE_FLAT, BASIC_BUTTON_TYPE_OUTLINED } from '@divvy-web/skylab.basicbutton'
import { FormTextInput } from '@divvy-web/skylab.form'
import { useForm, useFormState } from '@divvy-web/skylab.react-hook-form'
import { TOAST_TYPE_BASIC, TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { useGetPhoneNumberForVerificationLazyQuery } from '@sputnik/components/VerifyPhoneNumber/GetPhoneNumberForVerification.gql'
import { useRequestPhoneVerification } from '@sputnik/components/VerifyPhoneNumber/RequestPhoneVerification.gql'
import VerifyPhoneNumberModalView from '@sputnik/components/VerifyPhoneNumber/VerifyPhoneNumberModalView'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import styles from './styles.module.scss'
import { useSubmitPhoneVerificationCode } from './SubmitPhoneVerificationCode.gql'
import { VerifyCodeModalValidationSchema, verifyCodeModalValidationSchema } from './verifyCodeModalValidationSchema'

export interface VerifyCodeModalProps {
  isShowing: boolean
  phoneNumber: string
  onGoBack: () => void
  onClose: () => void
  onPhoneNumberVerified?: () => void
}

const VerifyCodeModal = ({
  isShowing,
  phoneNumber,
  onGoBack,
  onClose,
  onPhoneNumberVerified,
}: VerifyCodeModalProps) => {
  const [getClassName] = useNameFormatter('VerifyCodeModal')
  const { appId } = useParams()
  const showToast = useToast()

  const lastFour = phoneNumber.substring(phoneNumber.length - 4)

  const [submitPhoneVerificationCode] = useSubmitPhoneVerificationCode({
    onError: (err) => {
      logError(err, {
        attributes: {
          action: 'handleSubmitSmsCode',
          creditAppId: appId,
          result: 'Error submitting phone verification code',
          message: err?.message,
        },
        eventName: 'SubmitPhoneVerificationCode',
      })
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to validate code'
          id='sputnik.VerifyCodeModal__4RUoQC'
        />,
      )
    },
  })
  const [requestPhoneVerification] = useRequestPhoneVerification({
    onError: (err) => {
      logError(err, {
        attributes: {
          action: 'handleResendClick',
          creditAppId: appId,
          result: 'Error resending code',
          mfaMethod: 'sms',
        },
        eventName: 'ResendPhoneVerificationCode',
      })
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to resend code'
          id='sputnik.VerifyCodeModal__fmd9fq'
        />,
      )
    },
    onCompleted: () => {
      logInfo('handleResendClick', {
        attributes: {
          result: 'Resend auth code clicked',
          creditAppId: appId,
          mfaMethod: 'sms',
        },
        eventName: 'ResendPhoneVerificationCode',
      })
      showToast(
        TOAST_TYPE_BASIC,
        <FormattedMessage
          defaultMessage='Code sent!'
          id='sputnik.VerifyCodeModal__paI6XG'
        />,
        { autoHideDelay: 3000 },
      )
    },
  })

  const [getPhoneNumberVerificationData] = useGetPhoneNumberForVerificationLazyQuery({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  const { handleSubmit, control } = useForm<VerifyCodeModalValidationSchema>({
    mode: 'all',
    defaultValues: {
      code: '',
    },
    resolver: yupResolver(verifyCodeModalValidationSchema),
  })

  const { isValid } = useFormState({ control })

  const onResendCode = async () => {
    if (!appId) {
      return
    }

    const { data } = await getPhoneNumberVerificationData()

    if (!data) {
      return
    }

    const { id: personId, smsConsentGivenAt } = data?.creditApplication?.authorizedSigner ?? {}

    if (!personId || !smsConsentGivenAt) {
      return
    }

    await requestPhoneVerification({
      variables: {
        salesforceCreditId: appId,
        personId,
        phoneNumber,
        smsConsent: !!smsConsentGivenAt,
      },
    })
  }

  const onSubmit = async (data: VerifyCodeModalValidationSchema) => {
    if (!appId) {
      return
    }

    const { data: phoneNumberVerificationData } = await getPhoneNumberVerificationData()

    if (!phoneNumberVerificationData) {
      return
    }

    const { id: personId, smsConsentGivenAt } = phoneNumberVerificationData?.creditApplication?.authorizedSigner ?? {}

    if (!personId || !smsConsentGivenAt) {
      return
    }

    const { code } = data

    await submitPhoneVerificationCode({
      variables: {
        salesforceCreditId: appId,
        personId,
        phoneNumber,
        accessCode: code,
      },
      onCompleted: () => {
        logInfo('handleSubmitSmsCode', {
          attributes: {
            creditAppId: appId,
            result: 'Verify code button has been clicked',
          },
          eventName: 'SubmitPhoneVerificationCode',
        })
        showToast(
          TOAST_TYPE_SUCCESS,
          <FormattedMessage
            defaultMessage='Successfully verified phone number'
            id='sputnik.VerifyCodeModal__+bNHGb'
          />,
          {
            autoHideDelay: 3000,
          },
        )
        onPhoneNumberVerified?.()
        onClose()
      },
    })
  }

  return (
    <VerifyPhoneNumberModalView
      actionButtons={
        <>
          <BasicButton
            type={BASIC_BUTTON_TYPE_FLAT}
            onClick={() => onGoBack()}
          >
            <FormattedMessage
              defaultMessage='Use a different number'
              id='sputnik.VerifyCodeModal__WCI6uY'
            />
          </BasicButton>
          <BasicButton
            type={BASIC_BUTTON_TYPE_OUTLINED}
            onClick={() => onResendCode()}
          >
            <FormattedMessage
              defaultMessage='Resend code'
              id='sputnik.VerifyCodeModal__ZD48TW'
            />
          </BasicButton>
          <BasicButton
            disabled={!isValid}
            onClick={handleSubmit(onSubmit)}
          >
            <FormattedMessage
              defaultMessage='Verify'
              id='sputnik.VerifyCodeModal__q5Xl0M'
            />
          </BasicButton>
        </>
      }
      isShowing={isShowing}
      title={
        <FormattedMessage
          defaultMessage='Enter your verification code'
          id='sputnik.VerifyCodeModal__E4N7KO'
        />
      }
      onClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles[getClassName('prompt')]}>
          <FormattedMessage
            defaultMessage='We sent a code to XXX-XXX-{lastFour}'
            id='sputnik.VerifyCodeModal__Xboh+a'
            values={{
              lastFour,
            }}
          />
        </div>
        <FormTextInput
          className='fs-mask'
          control={control}
          label={
            <FormattedMessage
              defaultMessage='Verification code'
              id='sputnik.VerifyCodeModal__/6gLBc'
            />
          }
          name='code'
        />
      </form>
    </VerifyPhoneNumberModalView>
  )
}

export default VerifyCodeModal
